import React, { Component } from 'react';
import { Row, Col, Container } from 'react-bootstrap';
import '../css/style.css';
import Preloader from '../appshell/Preloader/Preloader';
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css';


class EmailScanner extends Component {

    constructor(props) {
        super(props);
        this.state = { spinner: true }

    }

    componentDidMount() {
        setTimeout(() => {
            this.setState({ spinner: false });
        }, 1000);
    }


    render() {


        return (

            <section>

                {this.state.spinner ? <Preloader /> : true}

                <Row className="hero-section d-flex position-relative align-items-center">
                    <Container>
                        <Row>
                            <div className="col-12 col-md-6 hero-content">

                                <h1>AI Email Scanner</h1>
                                <p className="mb-0 mt-4">
                                    <strong>My Role</strong>: Product & UX Designer, Lead Engineer</p>
                                <p className="mb-0"><strong>Responsibilities</strong>: End-to-end product design, PDF-to-HTML parsing engine development, accessibility (508) ruleset creation, real-time preview system architecture</p>
                                <p className="mb-0"><strong>Platform</strong>: Internal Web Application</p>
                                <p><strong>Deliverables</strong>: <a href="https://ai-email-scanner.vercel.app/" target="_blank" rel="noopener noreferrer" className="header-link">ai-email-scanner.vercel.app</a></p>
                            </div>
                        </Row>
                    </Container>

                    <div className="col-12 hero-image">
                        <div className="hero-image-container">
                            <LazyLoadImage
                                effect="blur"
                                src="./assets/img/scanner/scanner-hero.webp"
                                className="hero-image-cover"
                            />
                        </div>
                    </div>

                </Row>

                <section className="section1 section">
                    <Container>
                        <Row className="align-items-center" >

                            <Col xs={12} >
                                <h2 >Overview & Design Approach</h2>
                                <p >
                                    Designing production-ready emails is notoriously fragile. Traditional workflows often involve hand-coding layouts from static mocks, where accessibility is easily overlooked and brand inconsistencies can accumulate across large template libraries.
                                </p>
                                <p>
                                    <strong>AI Email Scanner</strong> was built as an internal solution to close this gap. It provides a faster, safer path from design to shippable code by automating the transcription process and enforcing code-level quality gates. The design approach centered on creating an "opinionated pipeline"—a tool that doesn't just generate code, but ensures that code aligns with a canonical knowledge base of approved brand tokens and accessible patterns.
                                </p>
                            </Col>
                        </Row>
                        <Row className="align-items-center mt-4" >
                            <Col xs={12} md={6}>
                                <LazyLoadImage effect="blur" src="./assets/img/scanner/email-scanning.webp" className="img-fluid" />
                                <p className="label">PDF Analysis & Layout Detection</p>
                            </Col>
                            <Col xs={12} md={6}>
                                <LazyLoadImage effect="blur" src="./assets/img/scanner/email-knowledge-base.webp" className="img-fluid" />
                                <p className="label">Canonical Knowledge Base Mapping</p>
                            </Col>
                        </Row>
                    </Container>
                </section>

                <section className="section2 section">
                    <Container>
                        <Row className="align-items-center" >

                            <Col xs={12} md={6} className="float-right" >
                                <h2 >Automated AI Email Generation</h2>
                                <p >
                                    The core of the platform is the <strong>AI Email Generator</strong>. Instead of manual transcription, users upload a designed email as a PDF. The system then parses the layout, text blocks, and visual hierarchy to match them against a curated library of components (hero blocks, CTAs, legal footers).
                                </p>
                                <p>
                                    This "template synthesis" preserves layout structure and brand tokens while producing semantically structured HTML. By automating this first code draft, we dramatically reduced the time from design approval to production readiness, ensuring that every email starts from a consistent source of truth.
                                </p>
                            </Col>
                            <Col xs={12} md={6} className="float-left">
                                <LazyLoadImage effect="blur" src="./assets/img/scanner/email-generator.webp" className="img-fluid" />
                                <p className="label">Automated PDF-to-HTML Conversion</p>
                            </Col>

                        </Row>
                    </Container>
                </section>

                <section className="section1 section">
                    <Container>
                        <Row className="align-items-center" >

                            <Col xs={12} md={6} className="order-md-2" >
                                <h2 >Live Edit & Real-Time Preview</h2>
                                <p >
                                    Once the initial HTML is generated, the <strong>Live Edit</strong> interface empowers non-developers to refine content without touching a line of code. Users can update subject lines, headlines, and CTAs through structured fields, seeing the design pane update immediately.
                                </p>
                                <p>
                                    To ensure shippability, the editor is constrained by the underlying design system. This "safe editing" environment eliminates the risk of accidentally breaking layouts or accessibility guarantees, significantly reducing the back-and-forth cycles between design and engineering teams.
                                </p>
                            </Col>
                            <Col xs={12} md={6} className="order-md-1">
                                <LazyLoadImage effect="blur" src="./assets/img/scanner/email-live-edit.webp" className="img-fluid" />
                                <p className="label">Real-Time Content Refinement</p>
                            </Col>

                        </Row>
                    </Container>
                </section>

                <section className="section2 section">
                    <Container>
                        <Row className="align-items-start" >

                            <Col xs={12} >
                                <h2 >AI-Powered Quality Gates</h2>
                                <p >
                                    The <strong>AI Email Validator</strong> acts as a final quality gate, running automated checks against three critical dimensions:
                                </p>
                                <ul className="mb-4">
                                    <li><strong>Design System Compliance</strong>: Verifies that only approved color, typography, and spacing tokens are used.</li>
                                    <li><strong>Accessibility (508)</strong>: Validates semantic structure, color contrast ratios, and image alt text.</li>
                                    <li><strong>Client Quirks</strong>: Detects patterns known to break in specific email clients (e.g., Outlook, Gmail).</li>
                                </ul>
                                <p>
                                    The tool generates a human-readable report with direct references back to the HTML, making it easy for teams to catch and fix regressions before they reach a test inbox.
                                </p>
                            </Col>
                        </Row>
                        <Row className="align-items-start mt-4" >
                            <Col xs={12} md={6} >
                                <LazyLoadImage effect="blur" src="./assets/img/scanner/email-validate.webp" className="img-fluid mb-2" />
                                <p className="label">Automated Validation Report</p>
                            </Col>
                            <Col xs={12} md={6} >
                                <LazyLoadImage effect="blur" src="./assets/img/scanner/email-validate-preview.webp" className="img-fluid mb-2" />
                                <p className="label">Cross-Device & Theme Previews</p>
                            </Col>
                        </Row>
                    </Container>
                </section>

                <section className="section1 section">
                    <Container>
                        <Row className="align-items-center" >

                            <Col xs={12} className="float-right" >
                                <h2 >Outcomes & Future Impact</h2>
                                <p>
                                    AI Email Scanner has transformed the production workflow from a manual, error-prone process into a streamlined, validated pipeline. By providing side-by-side desktop/mobile and light/dark mode previews, we've made layout issues visible long before deployment.
                                </p>
                                <p>
                                    The tool has resulted in <strong>reduced time-to-first-draft</strong>, <strong>fewer accessibility regressions</strong>, and <strong>improved collaboration</strong> across multidisciplinary teams.
                                </p>
                                <p>
                                    One of the core strengths of this system is its <strong>scalability</strong>. The architecture allows for the rapid integration of additional client-specific knowledge bases, making it a versatile platform capable of supporting multiple brands and evolving design requirements. Looking forward, we aim to integrate downstream ESP support and automated fix suggestions to further enhance the speed and quality of our email communications.
                                </p>
                            </Col>
                        </Row>
                    </Container>
                </section>

            </section>



        )
    }
}

export default EmailScanner;